import type { z } from 'zod';

/**
 * Type-safe wrapper around localStorage. Each key is bound to a Zod schema;
 * malformed values are deleted on read so a corrupted entry never throws.
 */
export class TypedStorageKey<TOut, TIn = TOut> {
  constructor(
    public readonly key: string,
    private readonly schema: z.ZodType<TOut, z.ZodTypeDef, TIn>,
    private readonly defaultValue: TOut,
  ) {}

  get(): TOut {
    try {
      const raw = localStorage.getItem(this.key);
      if (raw === null) return this.defaultValue;
      const parsed: unknown = JSON.parse(raw);
      const result = this.schema.safeParse(parsed);
      if (!result.success) {
        localStorage.removeItem(this.key);
        return this.defaultValue;
      }
      return result.data;
    } catch {
      return this.defaultValue;
    }
  }

  set(value: TOut): void {
    localStorage.setItem(this.key, JSON.stringify(this.schema.parse(value)));
  }

  remove(): void {
    localStorage.removeItem(this.key);
  }
}
